import React, { memo, useCallback, useMemo } from 'react';

import { Advice, Button } from '@Sage/hoi-poi-ui';
import { isEqual } from '@Sage/ssm-utils';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { EntityCrudActions } from 'actions';
import { PRODUCTS } from 'constants/Entities';
import { getActiveCrudName, getActiveLateralCrudName } from 'utils/crud';
import { getDecimalsByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { getEntityFromString } from 'utils/getEntityFromString';
import { getLiteral } from 'utils/getLiteral';
import { normalizeEnvironmentValue } from 'utils/multiEnvironment';
import { ensureRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';

import EntityCrudTab from './EntityCrudTab';
import './index.scss';

const propTypes = {
    width: PropTypes.string,
    entity: PropTypes.object,
};

export function mapDispatchToProps(dispatch) {
    return {
        ...bindActionCreators(EntityCrudActions, dispatch),
    };
}

function mapStateToProps(state, props) {
    let activeCrud = getActiveCrudName(state);
    if (props.isLateralCrud) {
        activeCrud = getActiveLateralCrudName(state);
    }
    let entityCrud = state.entityCrud[activeCrud] || {};
    let originalData = entityCrud.originalData;
    let selection = state.entityListSelect[activeCrud];
    let workFlowPermissions = state.config.userData.workFlows;
    let entity;
    if (entityCrud.entity) {
        entity = getEntityFromString(entityCrud.entity);
    }
    let workFlow =
        (entity &&
            entity.workFlow &&
            state.config.workFlows &&
            state.config.workFlows[entity.workFlow]) ||
        null;

    const duplicates = entityCrud && entityCrud.duplicates && entityCrud.duplicates;
    const isAdmin = state.config?.permission?.canConfigImplementation;

    const multiEnvironment = state.config?.permission?.multiEnvironment;
    const showButtonAddExtraFields =
        entity?.addExtraFieldsButtonOnCrud &&
        state.config?.permission?.manageExtraFields &&
        isAdmin;

    return {
        data: entityCrud.data,
        configMap: entityCrud.configMap,
        schema: entityCrud.schema,
        mappedSchema: entityCrud.mappedSchema,
        entity: entity,
        errors: entityCrud.errorsSave,
        firstErrorField: entityCrud.firstErrorField,
        dynamicHiddenMap: entityCrud.dynamicHiddenMap,
        isBulkAction: entityCrud.isBulkAction,
        selectedAccounts: selection && selection.total ? selection.total : 0,
        workFlow:
            (workFlowPermissions &&
                workFlowPermissions[entityCrud.entity] &&
                workFlowPermissions[entityCrud.entity] === '1' &&
                workFlow) ||
            null,
        idUserType: state.config.userData.idTipoUsuario,
        isNewEntity: !entityCrud.id,
        duplicates,
        originalData,
        firstFieldToFocus: entityCrud.firstFieldToFocus,
        showButtonAddExtraFields,
        fieldsToClear: entityCrud.fieldsToClear,
        multiEnvironment,
        getInfo: props.getInfo,
    };
}

const EntityCrudGeneric = ({
    schema,
    data,
    configMap,
    errors,
    firstErrorField,
    dynamicHiddenMap,
    isBulkAction,
    workFlow,
    idUserType,
    isNewEntity,
    duplicates,
    changeFields,
    mappedSchema,
    checkDuplicate,
    entity,
    firstFieldToFocus,
    userInviteFieldActions,
    showButtonAddExtraFields,
    renderCustomField,
    formWidth,
    originalData,
    fieldsToClear,
    setFieldsToClear,
    multiEnvironment,
    getInfo,
    toggleExtraFieldsModal,
}) => {
    const removeAllUnicode = useCallback((value) => {
        if (typeof value === 'string')
            // oxlint-disable-next-line no-control-regex
            return value.replace(/(?!\n)(?!\r)(?!\t)[\0\x00-\x1F\x7F\u202D\u202C]/g, '');
        return value;
    }, []);

    const changeField = useCallback(
        (field) => {
            return (value) => {
                // If only exists value then fields are implicit
                const finalValue = removeAllUnicode(value);

                const fieldsBeingChanged = field ? { [field]: finalValue } : finalValue || {};

                // Detect if this change affects environment or currency
                let newIdEnvironment = null;
                let newIdCurrency = null;
                let shouldReformat = false;

                Object.entries(fieldsBeingChanged).forEach(([fId, fVal]) => {
                    const fSchema = mappedSchema?.[fId];
                    const fIdLower = fId.toLowerCase();
                    if (
                        fSchema?.isMultiEnvironmentField ||
                        fIdLower === 'idenvironment' ||
                        fId === environmentField?.id
                    ) {
                        newIdEnvironment = normalizeEnvironmentValue(fVal);
                        shouldReformat = true;
                    }
                    if (fIdLower === 'idcurrency') {
                        newIdCurrency = fVal?.value ?? fVal;
                        shouldReformat = true;
                    }
                });

                let reformattedFields = {};
                if (shouldReformat) {
                    const finalEnvironment =
                        newIdEnvironment ??
                        normalizeEnvironmentValue(
                            data?.[environmentField?.id] ?? data?.idBranch?.value ?? data?.idBranch,
                        );
                    const finalCurrency =
                        newIdCurrency ?? data?.idCurrency?.value ?? data?.idCurrency;

                    Object.entries(mappedSchema || {}).forEach(([fId, fSchema]) => {
                        if (fSchema.type !== 'decimal' && fSchema.type !== 'currency') return;
                        const currentValue = data?.[fId];
                        if (
                            currentValue === null ||
                            currentValue === undefined ||
                            currentValue === ''
                        )
                            return;
                        const numVal = parseFloat(currentValue);
                        if (isNaN(numVal)) return;

                        const decimalType =
                            fSchema.type === 'currency'
                                ? fSchema.inputAttrs?.decimalType || DECIMAL_TYPES.CURRENCY_GENERAL
                                : fSchema.inputAttrs?.decimalType || DECIMAL_TYPES.GENERAL;

                        const newLimit = getDecimalsByConfig({
                            type: decimalType,
                            idEnvironment: finalEnvironment,
                            idCurrency: finalCurrency,
                            entityName: entity?.extraFieldName,
                            fieldId: fSchema.fieldConfiguration || fId,
                        });

                        if (newLimit !== undefined && newLimit !== null) {
                            reformattedFields[fId] = numVal.toFixed(newLimit);
                        }
                    });
                }

                if (field) {
                    const checkDuplicates = mappedSchema?.[field]?.checkDuplicates;
                    const checkDuplicateId = mappedSchema?.[field]?.checkDuplicateId;
                    const checkDuplicateLabel = mappedSchema?.[field]?.label;

                    if (checkDuplicateId && checkDuplicates)
                        checkDuplicate(
                            entity.duplicate,
                            checkDuplicateId,
                            checkDuplicateLabel,
                            finalValue,
                        );
                    changeFields({ [field]: finalValue, ...reformattedFields });
                } else {
                    changeFields({ ...finalValue, ...reformattedFields });
                }
            };
        },
        [
            changeFields,
            checkDuplicate,
            data,
            entity,
            environmentField?.id,
            mappedSchema,
            removeAllUnicode,
        ],
    );

    const clearField = useCallback(
        (field) => {
            return (clear) => {
                if (!field?.id) return;

                const { id } = field;
                const updatedFieldsToClear = { ...fieldsToClear };

                if (clear) {
                    updatedFieldsToClear[id] = clear;
                } else {
                    delete updatedFieldsToClear[id];
                }

                setFieldsToClear(updatedFieldsToClear);
            };
        },
        [fieldsToClear, setFieldsToClear],
    );

    const goToExtraFields = useCallback(() => {
        logEvent({
            event: entity?.trueName || 'none',
            submodule: 'crud',
            functionality: 'createField',
        });

        const url = '/settings/fields';
        const queryString = entity?.extraFieldTab
            ? `?tab=${entity?.extraFieldTab}&showCrud=true`
            : undefined;

        // Check if there are unsaved changes before navigating
        const hasChanges = !isEqual(originalData, data, true);

        if (hasChanges) {
            // Show confirmation modal if there are unsaved changes
            toggleExtraFieldsModal(true, url, queryString);
        } else {
            // Navigate directly if no changes
            setTimeout(() => {
                ensureRoute(url, queryString);
            });
        }
    }, [data, entity?.extraFieldTab, entity?.trueName, originalData, toggleExtraFieldsModal]);

    const className = classnames('fm-crud-form', {
        'fm-crud-form--small': formWidth === 'small',
    });

    const environmentField = useMemo(() => {
        if (!multiEnvironment) return null;
        let fieldSchema = null;
        for (const tab of schema || []) {
            if (tab.fields) {
                const field = tab.fields.find((field) => field.isMultiEnvironmentField);
                if (field) {
                    fieldSchema = field;
                    break;
                }
            }
        }
        if (!fieldSchema && entity?.entity === PRODUCTS.entity) {
            for (const tab of schema || []) {
                if (tab.fields) {
                    const specialField = tab.fields.find(
                        (field) => field.id === 'strIdEnvironments',
                    );
                    if (specialField) {
                        fieldSchema = specialField;
                        break;
                    }
                }
            }
        }
        return fieldSchema ? { ...fieldSchema, value: data?.[fieldSchema?.id] } : null;
    }, [data, entity?.entity, multiEnvironment, schema]);

    const infoComponent = useMemo(() => {
        if (!getInfo) return null;
        const info = getInfo(data);
        if (!info) return null;
        return (
            <Advice type="info" showIcon showCollapse>
                {info}
            </Advice>
        );
    }, [data, getInfo]);

    const showButtonAddExtraFieldsMultiEnvironment =
        showButtonAddExtraFields &&
        (!multiEnvironment || (multiEnvironment && environmentField?.value));

    return (
        <div className={className}>
            {infoComponent}

            <form>
                {schema &&
                    schema
                        .filter((tab) =>
                            tab.fields?.find(
                                (field) =>
                                    (!field.hidden && !field.inputs) ||
                                    field.inputs?.find((input) => !input.hidden),
                            ),
                        )
                        .map((tab, index) => {
                            let finalTab = { ...tab };
                            if (tab.beforeRenderCrudSection) {
                                finalTab = tab.beforeRenderCrudSection(tab);
                            }

                            if (!finalTab.show) return null;

                            return (
                                <EntityCrudTab
                                    key={`tab-${index}`}
                                    tab={finalTab}
                                    indexTab={index}
                                    data={data}
                                    configMap={configMap}
                                    changeField={changeField}
                                    clearField={clearField}
                                    errors={errors}
                                    dynamicHiddenMap={dynamicHiddenMap}
                                    firstErrorField={firstErrorField}
                                    isBulkAction={isBulkAction}
                                    workFlow={workFlow}
                                    idUserType={idUserType}
                                    isNewEntity={isNewEntity}
                                    duplicates={duplicates}
                                    entity={entity}
                                    firstFieldToFocus={firstFieldToFocus}
                                    userInviteFieldActions={userInviteFieldActions}
                                    renderCustomField={renderCustomField}
                                    originalData={originalData}
                                    environmentField={multiEnvironment ? environmentField : null}
                                />
                            );
                        })}
            </form>
            {showButtonAddExtraFieldsMultiEnvironment && (
                <Button
                    type="dashed"
                    isFullWidth
                    icon="addPlus"
                    size="big"
                    onClick={goToExtraFields}
                >
                    {getLiteral('action_add_custom_field_for_all_registers')}
                </Button>
            )}
        </div>
    );
};

EntityCrudGeneric.propTypes = propTypes;

export default memo(connect(mapStateToProps, mapDispatchToProps)(EntityCrudGeneric));
